import React, { useCallback } from 'react'
import {
  SketchElement,
  isLayerElement,
  isVariantSetElement,
  ElementIcon,
} from '@sketch/inspector'
import { useDropdownState, Dropdown } from '@sketch/components'
import { isSecondaryClick } from '@sketch/utils'

// Import LayerItemActions from LayersTreeItem
import { LayerItemActions } from './LayersTreeItem'

// Reuse the same header styles from the inspector context menu
import {
  StyledDropdownHeader,
  DropdownHeaderText,
  DropdownWrapper,
} from '../../../shares/FrameView/components/FrameDetailInspectorContextMenu/FrameDetailInspectorContextMenu.styles'

interface LayerTreeContextMenuProps {
  position: { x: number; y: number } | null
  targetElement: SketchElement | null
  onClose: () => void
  onGoToLayer: (element: SketchElement) => void
  onSelectLayer: (element: SketchElement) => void
}

export function LayerTreeContextMenu({
  position,
  targetElement,
  onClose,
  onGoToLayer,
  onSelectLayer,
}: LayerTreeContextMenuProps) {
  const dropdown = useDropdownState({
    onToggle: onClose,
  })

  const handleContextMenu = (e: React.MouseEvent) => {
    // Prevent right-click events from propagating
    e.preventDefault()
    e.stopPropagation()
  }

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (isSecondaryClick(event)) {
        return
      }
      onClose()
    },
    [onClose]
  )

  if (!targetElement) {
    return null
  }

  const isVariantSet = isVariantSetElement(targetElement)

  return (
    <DropdownWrapper
      {...dropdown}
      onClickOutside={handleClickOutside}
      placement="bottom-start"
      style={
        position
          ? {
              left: position.x,
              top: position.y + 5,
              position: 'fixed',
            }
          : {}
      }
      contentStyle={{ maxWidth: 220 }}
      visible={!!position}
      maxWidth="100%"
      usePortal={true}
    >
      <div onContextMenu={handleContextMenu}>
        <StyledDropdownHeader>
          <ElementIcon
            elementType={targetElement.type}
            isVariantSet={isVariantSet}
            isLayerMasked={
              isLayerElement(targetElement)
                ? targetElement.isLayerMasked
                : false
            }
            isLayerExportable={targetElement.isExportable}
          />
          <DropdownHeaderText>{targetElement.name}</DropdownHeaderText>
        </StyledDropdownHeader>
        <Dropdown.Divider />

        <div
          onClick={e => {
            // Only close if clicking on a dropdown item, not empty space
            if (e.target !== e.currentTarget) {
              onClose()
            }
          }}
        >
          <LayerItemActions
            sketchElement={targetElement}
            onGoToLayer={onGoToLayer}
            onSelect={onSelectLayer}
          />
        </div>
      </div>
    </DropdownWrapper>
  )
}
